import { accountRisksSettingsEdit } from "@/redux/account.actions"
import { createRiskAttribute, normalizeRiskAttributes, RiskAttribute } from "@/redux/account.types"
import Card from "@/components/card"
import Space from "@/components/space"
import Modal from "@/components/modal"
import Button from "@/components/button"
import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import { faTrash, faInfoCircle, faTimes, faExclamationTriangle, faCog } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { t } from "i18next"
import { useMemo, useState } from "react"
import RiskAttributesEditor from "../RiskAttributesEditor"
import Tooltip from "@/components/tooltip"

type Props = {
  onDirty: () => void
}

export default function RiskAttributesConfig({ onDirty }: Props) {
  const dispatch = useAppDispatch()

  const [deleteConfirmModal, setDeleteConfirmModal] = useState<RiskAttribute | null>(null)
  const settingsAttributes = useAppSelector((state) => state._session.accountRisksSettings.riskAttributes)
  //Normalise pour ecarter les entrees invalides et dedoublonner (cf. normalizeRiskAttributes)
  const riskAttributes = useMemo(() => normalizeRiskAttributes(settingsAttributes), [settingsAttributes])

  const updateAttributes = (newAttributes: RiskAttribute[]) => {
    dispatch(
      accountRisksSettingsEdit({
        key: "riskAttributes",
        value: newAttributes
      })
    )
    onDirty()
  }

  const handleRemove = (attribute: RiskAttribute) => {
    setDeleteConfirmModal(attribute)
  }

  const handleConfirmDelete = () => {
    if (!deleteConfirmModal) return
    updateAttributes(riskAttributes.filter((attribute) => attribute.id !== deleteConfirmModal.id))
    setDeleteConfirmModal(null)
  }

  const handleAdd = () => {
    updateAttributes([...riskAttributes, createRiskAttribute(t("utils_new"))])
  }

  return (
    <Card>
      <div className="mb-4 flex items-center">
        <div className="flex items-center gap-3">
          <FontAwesomeIcon icon={faCog} className="grey-t text-lg" />
          <span className="text-lg">{t("Configuration des attributs")}</span>
          <Tooltip
            text={t(
              "Le 1er attribut définit la famille de regroupement (ex : « Famille de risque »).\nLe 2ème attribut est le nom du risque.\nLes suivants sont libres (ex : situation dangereuse, effets, mesures…)."
            )}
            position="right"
            width={280}>
            <FontAwesomeIcon icon={faInfoCircle} className="grey-t cursor-help" />
          </Tooltip>
        </div>
        <Space />
        <span className="grey-t">{t("(Déplacer pour réordonner)")}</span>
      </div>
      <RiskAttributesEditor
        items={riskAttributes}
        onChange={updateAttributes}
        onRemove={handleRemove}
        onAdd={handleAdd}
      />

      {deleteConfirmModal && (
        <Modal
          title={t("Supprimer un attribut")}
          footer={
            <div className="flex w-full m-[-5px]">
              <Space />
              <Button onClick={() => setDeleteConfirmModal(null)} className="med" icon={faTimes} isWithBorder>
                {t("utils_cancel")}
              </Button>
              <Button onClick={handleConfirmDelete} className="primary" icon={faTrash}>
                {t("utils_delete")}
              </Button>
            </div>
          }>
          <div className="flex flex-col gap-4">
            <p className="font-semibold text-base">
              {t(`Êtes-vous sûr de vouloir supprimer l'attribut « ${deleteConfirmModal.name} » ?`)}
            </p>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <FontAwesomeIcon icon={faExclamationTriangle} className="red-t flex-shrink-0 text-lg" />
                <p className="text-sm red-t">
                  <strong>{t("Attention :")}</strong>{" "}
                  {t(
                    "Cette action est irréversible. Tous les risques associés à cet attribut perdront les données saisies dans ce champ. Cette information sera supprimée de façon permanente pour tous les risques de votre organisation."
                  )}
                </p>
              </div>
            </div>
            <p className="text-sm grey-t">
              <strong>{t("Note :")}</strong>{" "}
              {t(
                "Les modifications seront appliquées uniquement après avoir cliqué sur le bouton Sauvegarder. Vous pouvez annuler avant la sauvegarde pour revenir à l'état initial."
              )}
            </p>
          </div>
        </Modal>
      )}
    </Card>
  )
}
